<script lang="ts">
	import { goto } from '$app/navigation';
	import RadioToggleIcon from '$lib/Components/radio-toggle-icon.svelte';
	import {
		useAppBookingOptions,
		type LocationOption
	} from '$lib/app-booking-options/app-booking-options';
	import { useBookingData } from '$lib/booking-data/booking-data';
	import { validator } from '@felte/validator-yup';
	import classNames from 'classnames';
	import { createForm } from 'felte';
	import { onMount } from 'svelte';
	import { number, object, type InferType } from 'yup';

	type Loc = LocationOption;

	const { appBookingOptions } = useAppBookingOptions();
	const { bookingData } = useBookingData();

	onMount(() => {
		$bookingData.location_id = null;
	});

	$: bookingLocationId = $bookingData.location_id;

	// calculate locations
	$: locations = $appBookingOptions.locations.filter(
		(location: Loc) => location.name !== 'Unknown' && location.name !== 'Other'
	) as Loc[];

	// control via form
	const schema = object({
		city_id: number().required('Please select a city')
	});

	type Schema = InferType<typeof schema>;

	const { form, data, errors, isSubmitting, setFields, setInitialValues, reset, isValid, isDirty } =
		createForm<Schema>({
			extend: validator({ schema }),
			initialValues: {},
			onSubmit: async (values) => {
				let city: LocationOption | undefined;

				if (values.city_id === 31) {
					city = { id: 31, name: 'Online', locationTypeId: null, order: null };
				} else {
					city = locations.find((location) => location.id === values.city_id);
				}

				if (!city) return;

				$bookingData.location_id = city.id;

				// define class type will prefer to customer
				$bookingData.class_type_id = city.locationTypeId === 3 ? 3 : 2;

				goto(`/classes`);
			}
		});

	// update base value follow options
	const updateInitValues = () => {
		if (!$bookingData.location_id) return;

		setInitialValues({
			city_id: $bookingData.location_id
		});

		reset();
	};

	$: {
		bookingLocationId;
		updateInitValues();
	}

	const chooseCity = (city: LocationOption) => {
		$bookingData.location_id = city.id;
		// define class type will prefer to customer
		$bookingData.featureOnCalendar =
			city.locationTypeId === 3 || city.locationTypeId === 9 ? true : false;

		if ($bookingData.featureOnCalendar) {
			goto(`/classes?view=calendar`);
		} else {
			goto(`/classes?view=list`);
		}
	};
</script>

<div class="max-w-[900px] w-full m-auto min-h-[calc(100vh-100px)] relative">
	<form use:form>
		<h1 class="text-[48px] text-affair font-jakarta-bold text-center">
			Select your preferred location
		</h1>

		<div class="flex flex-col sm:flex-row justify-between gap-4 mt-8">
			<div class="flex flex-col gap-3 items-center w-full sm:w-4/12">
				<h2 class="text-xl text-shark font-jakarta-semi-bold">In Home</h2>
				{#each locations.filter((location) => location.locationTypeId === null && location.id !== 31) as city}
					<button
						type="button"
						on:click={() => chooseCity(city)}
						class={classNames(
							'h-[58px] py-[12px] px-4 rounded-[10px] border flex gap-2 items-center  w-full',
							'hover:border-affair',
							$data.city_id === city.id && '!bg-affair-light !border-affair'
						)}
					>
						<RadioToggleIcon checked={$data.city_id === city.id} />

						<p
							class={classNames(
								'text-base text-shark font-jakarta-regular',
								$data.city_id === city.id && '!font-jakarta-bold'
							)}
						>
							{city.name}
						</p>
					</button>
				{/each}
			</div>
			<div class="w-full h-[2px] sm:h-auto sm:w-[2px] bg-alabaster" />
			<div class="flex flex-col gap-3 items-center w-full sm:w-4/12">
				<h2 class="text-xl text-shark font-jakarta-semi-bold">Clubs</h2>
				{#each locations.filter((location) => location.locationTypeId === 3) as city}
					<button
						type="button"
						on:click={() => chooseCity(city)}
						class={classNames(
							'h-[58px] py-[12px] px-4 rounded-[10px] border flex gap-2 items-center  w-full',
							'hover:border-affair',
							$data.city_id === city.id && '!bg-affair-light !border-affair'
						)}
					>
						<RadioToggleIcon checked={$data.city_id === city.id} />

						<p
							class={classNames(
								'text-base text-shark font-jakarta-regular',
								$data.city_id === city.id && '!font-jakarta-bold'
							)}
						>
							{city.name}
						</p>
					</button>
				{/each}
			</div>
			<div class="w-full h-[2px] sm:h-auto sm:w-[2px] bg-alabaster" />
			<div class="flex flex-col gap-3 items-center w-full sm:w-4/12">
				<h2 class="text-xl text-shark font-jakarta-semi-bold">Online</h2>
				{#each locations.filter((location) => location.id == 31) as city}
					<button
						type="button"
						on:click={() => chooseCity(city)}
						class={classNames(
							'h-[58px] py-[12px] px-4 rounded-[10px] border flex gap-2 items-center w-full',
							'hover:border-affair',
							$data.city_id === city.id && '!bg-affair-light !border-affair'
						)}
					>
						<RadioToggleIcon checked={$data.city_id === city.id} />

						<p
							class={classNames(
								'text-base text-shark font-jakarta-regular',
								$data.city_id === city.id && '!font-jakarta-bold'
							)}
						>
							{city.name}
						</p>
					</button>
				{/each}
			</div>
		</div>
		<!-- <div class="flex items-center gap-4 my-5">
			<div class="form-line" />
			<p>OR</p>
			<div class="form-line" />
		</div>
		<div class="flex justify-center">
			<button
				type="button"
				on:click={() => setFields('city_id', 31)}
				class={classNames(
					'h-[58px] py-[12px] px-4 rounded-[10px] border flex gap-2 items-center w-full sm:w-[273px]',
					$data.city_id === 31 && '!bg-affair-light !border-affair'
				)}
			>
				<RadioToggleIcon checked={$data.city_id === 31} />

				<p
					class={classNames(
						'text-base text-shark font-jakarta-regular',
						$data.city_id === 31 && '!font-jakarta-bold'
					)}
				>
					Online
				</p>
			</button>
		</div> -->

		<!-- <div class="flex flex-col gap-3 items-center my-5">
			<button class="form_btn" type="submit">
				{$isSubmitting ? 'Please wait ...' : 'Next'}
			</button>

			{#if $errors.city_id}
				<div class="text-sm text-red text-center">{$errors.city_id[0]}</div>
			{/if}
		</div> -->
	</form>
</div>
